import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** How long a freshly created stage / activity stays highlighted. */
export const HIGHLIGHT_DURATION_MS = 6000;

type HighlightId = string | number | null | undefined;

/**
 * Marks ids as "just created" for a few seconds and then clears them, so the
 * interview plan can draw attention to a stage box or an activity card the
 * user has only now added.
 *
 * Each id keeps its own timer — highlighting a second item does not cut the
 * first one's window short.
 */
export const useTransientHighlight = (duration = HIGHLIGHT_DURATION_MS) => {
  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(
    () => new Set()
  );
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  // Drop pending timers on unmount so a late tick cannot setState on a
  // stage/card that has already been deleted.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const highlight = useCallback(
    (id: HighlightId) => {
      if (id === null || id === undefined || id === '') return;
      const key = String(id);

      // Re-highlighting the same id restarts its window rather than stacking
      // two timers, the second of which would clear the highlight early.
      const running = timersRef.current.get(key);
      if (running) clearTimeout(running);

      setHighlightedIds((prev) =>
        prev.has(key) ? prev : new Set(prev).add(key)
      );

      timersRef.current.set(
        key,
        setTimeout(() => {
          timersRef.current.delete(key);
          setHighlightedIds((prev) => {
            if (!prev.has(key)) return prev;
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }, duration)
      );
    },
    [duration]
  );

  const isHighlighted = useCallback(
    (id: HighlightId) =>
      id === null || id === undefined ? false : highlightedIds.has(String(id)),
    [highlightedIds]
  );

  // Plain id list for consumers that cross a context boundary — passing a
  // predicate down makes the React Compiler treat the caller's props as
  // possibly mutated and skip optimising the component.
  const highlightedIdList = useMemo(
    () => Array.from(highlightedIds),
    [highlightedIds]
  );

  return { highlight, isHighlighted, highlightedIds: highlightedIdList };
};

/** Test hook for whichever stage box / activity card is currently highlighted. */
export const HIGHLIGHT_TEST_ID = 'newly-created-highlight';

/**
 * Props shared by the highlighted stage box and activity card so both read as
 * the same "this is new" state: the box keeps its background and gains a solid
 * 3px `primary-200` (#C3CCFF) edge.
 *
 * Drawn as an `outline`, not a border — outlines take no space, so nothing
 * moves when the highlight appears or expires. (A 3px border would eat 2px of
 * the content box under `box-sizing: border-box` and shove the row's contents
 * around; an outer box-shadow doesn't shift anything either but gets clipped
 * by the stage box's `overflow: hidden` on the activity cards inside it.)
 *
 * `outlineOffset: -3px` pulls the outline fully inside the box, so it paints
 * exactly the outermost 3px — covering the 1px border — and can never be
 * clipped by an ancestor. The outline is always present but transparent, which
 * is what lets the colour transition in and out.
 *
 * `outlineColor` takes the theme token, never a raw `var(--…)`: this theme sets
 * `cssVarPrefix: 'kula'`, so a hand-written `--chakra-colors-*` reference
 * resolves to nothing and the outline silently falls back to `currentColor`.
 */
export const getHighlightProps = (isHighlighted: boolean) => ({
  outline: '3px solid transparent',
  outlineOffset: '-3px',
  transition: 'outline-color 0.25s ease-in-out',
  ...(isHighlighted && {
    outlineColor: 'primary-200',
    'data-highlighted': 'true',
    'data-test-id': HIGHLIGHT_TEST_ID,
  }),
});
